/**
 * KnowledgeGraphPreview — the hero money shot (ported from the v3 landing
 * mockup). The delivery data starts scattered, then RESOLVES into the clean
 * columns (Detected → Who/what → The work → At risk): the story chain that
 * puts the sprint at risk brightens (orange), the noise fades (teal), a scan
 * sweeps, and the sprint pulses. Chaos → clarity, on a loop. The visual twin of
 * the LEADERSHIP briefing beside it.
 *
 * DOM mutated via refs in an rAF loop (no React re-renders). Honors
 * prefers-reduced-motion (holds the resolved state).
 */

import { useEffect, useRef } from 'react';

const W = 560, H = 320;
/** Below this rendered width the resolved layout flips to the VERTICAL
 *  variant: the four columns can't fit a phone card readably, so the three
 *  story lanes run top→down instead (signal → owner → ticket → sprint),
 *  with the column headers stacked as row labels. Same nodes, same morph,
 *  same canonical visual — rotated for a tall narrow screen. */
const MOBILE_BREAK = 480;
type Node = { id: string; type: string; html: string; sx: number; sy: number; tx: number; ty: number; mx: number; my: number };
const NODES: Node[] = [
  // Detected — the Avium Signals (mobile: row 1, three lanes across)
  { id: 'sOver', type: 'signal', html: '⚠ Over-allocation', sx: 130, sy: 62, tx: 78, ty: 100, mx: 92, my: 52 },
  { id: 'sBlock', type: 'signal', html: '⏸ Blocked 6d', sx: 82, sy: 262, tx: 78, ty: 165, mx: 280, my: 52 },
  { id: 'sScope', type: 'signal', html: '↔ Scope +3', sx: 180, sy: 150, tx: 78, ty: 230, mx: 468, my: 52 },
  // Who / what — the people who own the work (mobile: row 2)
  { id: 'pNick', type: 'person over', html: '<span class="kgp-ava">NJ</span> Nick · 143%', sx: 275, sy: 105, tx: 212, ty: 100, mx: 100, my: 124 },
  { id: 'pSam', type: 'person', html: '<span class="kgp-ava">SP</span> Sam Patel', sx: 215, sy: 215, tx: 212, ty: 165, mx: 280, my: 124 },
  { id: 'pPriya', type: 'person', html: '<span class="kgp-ava">PK</span> Priya Kaur', sx: 360, sy: 270, tx: 212, ty: 230, mx: 460, my: 124 },
  // The work — the tickets, lane-aligned under their owners (mobile: row 3)
  { id: 'av216', type: 'ticket risk', html: 'AV-216', sx: 430, sy: 58, tx: 356, ty: 100, mx: 100, my: 196 },
  { id: 'av215', type: 'ticket risk', html: 'AV-215', sx: 490, sy: 200, tx: 356, ty: 165, mx: 280, my: 196 },
  { id: 'av230', type: 'ticket risk', html: 'AV-230', sx: 330, sy: 130, tx: 356, ty: 230, mx: 460, my: 196 },
  // At risk — the sprint; every lane converges DOWN into it on mobile
  { id: 's12', type: 'sprint', html: 'Sprint 12 ⚠ 61%', sx: 445, sy: 250, tx: 476, ty: 165, mx: 280, my: 276 },
];
const DECOYS = [{ x: 170, y: 40 }, { x: 370, y: 160 }, { x: 105, y: 130 }, { x: 285, y: 275 }, { x: 480, y: 100 }, { x: 405, y: 225 }, { x: 55, y: 200 }, { x: 255, y: 52 }];
// Each lane is a coherent story: a signal → the owner → their ticket → the sprint.
const STORY: [string, string][] = [
  ['sOver', 'pNick'], ['pNick', 'av216'], ['av216', 's12'],
  ['sBlock', 'pSam'], ['pSam', 'av215'], ['av215', 's12'],
  ['sScope', 'pPriya'], ['pPriya', 'av230'], ['av230', 's12'],
];
const EXTRA: [string, string][] = [['pNick', 'av215'], ['av216', 'av215'], ['sScope', 's12'], ['pSam', 'av216'], ['sBlock', 'sScope'], ['av230', 'av215'], ['pNick', 'av230'], ['sOver', 's12']];
const HEADERS = [{ label: 'Detected', x: 78 }, { label: 'Who / what', x: 212 }, { label: 'The work', x: 356 }, { label: 'At risk', x: 476 }];
// mobile: the same four headers become stacked row labels, centered above each row
const MOBILE_HEADER_Y = [18, 90, 162, 236];

function nodeClass(type: string): string {
  if (type.includes('sprint')) return 'kgp-sprint';
  if (type.includes('signal')) return 'kgp-signal';
  if (type.includes('person')) return type.includes('over') ? 'kgp-person kgp-over' : 'kgp-person';
  return type.includes('risk') ? 'kgp-ticket kgp-risk' : 'kgp-ticket';
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const win = (t: number, a: number, b: number) => clamp01((t - a) / (b - a));
const easeIO = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
// Horizontal S-curve so edges flow cleanly left→right (and don't pile up as
// crossing straight lines as they converge on the sprint). In the vertical
// mobile layout the same S-curve runs top→down instead.
const curve = (a: { cx: number; cy: number }, b: { cx: number; cy: number }, vert: boolean) => {
  if (vert) {
    const dy = Math.max(26, (b.cy - a.cy) * 0.45);
    return `M ${a.cx} ${a.cy} C ${a.cx} ${a.cy + dy}, ${b.cx} ${b.cy - dy}, ${b.cx} ${b.cy}`;
  }
  const dx = Math.max(40, (b.cx - a.cx) * 0.45);
  return `M ${a.cx} ${a.cy} C ${a.cx + dx} ${a.cy}, ${b.cx - dx} ${b.cy}, ${b.cx} ${b.cy}`;
};

/**
 * variant 'dark' (default): the in-app product-window look (dark chrome).
 * variant 'light' (2026-06-11): the marketing-asset register — white card,
 * amber caps headers, orange story chains on white, dark sprint anchor.
 * Same morph, same data; only the surface palette changes.
 */
export default function KnowledgeGraphPreview({ variant = 'dark' }: { variant?: 'dark' | 'light' } = {}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const storyRefs = useRef<(SVGPathElement | null)[]>([]);
  const extraRefs = useRef<(SVGPathElement | null)[]>([]);
  const decoyRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hdrRefs = useRef<(HTMLDivElement | null)[]>([]);
  const capRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scanRef = useRef<HTMLDivElement | null>(null);
  const variantRef = useRef(variant);
  variantRef.current = variant;

  useEffect(() => {
    const byId: Record<string, { cx: number; cy: number; hw: number; hh: number }> = {};
    NODES.forEach(n => { byId[n.id] = { cx: n.sx, cy: n.sy, hw: 0, hh: 0 }; });
    // an edge's endpoints, offset to the EDGE of each node (so lines start on the
    // box border, not run through its center). Half-sizes are in logical units.
    // Horizontal layout → offset along x by half-WIDTH; vertical → along y by half-HEIGHT.
    const edgePts = (a: typeof byId[string], b: typeof byId[string], vert: boolean) => vert
      ? ([
          { cx: a.cx, cy: a.cy + Math.sign(b.cy - a.cy) * (a.hh + 1) },
          { cx: b.cx, cy: b.cy + Math.sign(a.cy - b.cy) * (b.hh + 1) },
        ] as const)
      : ([
          { cx: a.cx + Math.sign(b.cx - a.cx) * (a.hw + 1), cy: a.cy },
          { cx: b.cx + Math.sign(a.cx - b.cx) * (b.hw + 1), cy: b.cy },
        ] as const);
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    let raf = 0; let start = 0;
    const CYCLE = 12000;
    const pct = (x: number, total: number) => `${(x / total) * 100}%`;

    const frame = (now: number) => {
      if (!start) start = now;
      const t = reduce ? 0.7 : ((now - start) % CYCLE) / CYCLE;
      const morph = easeIO(win(t, 0.36, 0.62));

      // px → logical conversion so the node's rendered half-width lands in SVG units
      const root = rootRef.current;
      const sx = root && root.clientWidth ? W / root.clientWidth : 1;
      const sy = root && root.clientHeight ? H / root.clientHeight : 1;
      // phone card → the resolved layout flips to the vertical lane variant.
      // The class gates the vertical-mode styling (label chips etc.) so it is
      // tied to the ACTUAL layout mode, never to a coincidental media query.
      const vert = !!(root && root.clientWidth && root.clientWidth < MOBILE_BREAK);
      if (root) root.classList.toggle('kgp-vert', vert);
      NODES.forEach(n => {
        const c = byId[n.id];
        c.cx = lerp(n.sx, vert ? n.mx : n.tx, morph); c.cy = lerp(n.sy, vert ? n.my : n.ty, morph);
        const el = nodeRefs.current[n.id];
        if (el) { el.style.left = pct(c.cx, W); el.style.top = pct(c.cy, H); c.hw = (el.offsetWidth / 2) * sx; c.hh = (el.offsetHeight / 2) * sy; }
      });
      const storyA = lerp(0.28, 0.95, clamp01(win(t, 0.24, 0.62)));
      storyRefs.current.forEach((p, i) => {
        if (!p) return; const [pa, pb] = edgePts(byId[STORY[i][0]], byId[STORY[i][1]], vert);
        p.setAttribute('d', curve(pa, pb, vert));
        p.setAttribute('stroke', '#F97316'); p.setAttribute('stroke-width', `${variantRef.current === 'light' ? lerp(1.7, 3.1, morph) : lerp(1.4, 2.6, morph)}`); p.setAttribute('stroke-opacity', `${storyA}`);
      });
      const extraA = lerp(0.3, 0, clamp01(win(t, 0.24, 0.5)));
      extraRefs.current.forEach((p, i) => {
        if (!p) return; const [pa, pb] = edgePts(byId[EXTRA[i][0]], byId[EXTRA[i][1]], vert);
        p.setAttribute('d', curve(pa, pb, vert));
        p.setAttribute('stroke', variantRef.current === 'light' ? '#0F766E' : '#5EEAD4'); p.setAttribute('stroke-width', '1'); p.setAttribute('stroke-opacity', `${extraA}`);
      });
      decoyRefs.current.forEach(d => { if (d) d.style.opacity = `${extraA}`; });
      const scanW = win(t, 0.22, 0.36);
      if (scanRef.current) { scanRef.current.style.opacity = scanW > 0 && scanW < 1 ? '0.9' : '0'; scanRef.current.style.left = pct(lerp(-80, W, scanW), W); }
      const sp = nodeRefs.current['s12'];
      if (sp) {
        if (t > 0.6) { const p = (Math.sin(now / 300) + 1) / 2; sp.style.boxShadow = `0 0 22px -4px rgba(249,115,22,.5), 0 0 0 ${lerp(0, 7, p)}px rgba(249,115,22,${lerp(0.5, 0, p)})`; }
        else sp.style.boxShadow = '0 0 22px -4px rgba(249,115,22,.5)';
      }
      const hA = win(t, 0.52, 0.64);
      hdrRefs.current.forEach((h, i) => {
        if (!h) return;
        h.style.opacity = `${hA}`;
        h.style.left = pct(vert ? W / 2 : HEADERS[i].x, W);
        h.style.top = pct(vert ? MOBILE_HEADER_Y[i] : 40, H);
      });
      if (capRefs.current[0]) capRefs.current[0]!.style.opacity = `${clamp01(win(t, 0.04, 0.1)) * (1 - win(t, 0.2, 0.26))}`;
      if (capRefs.current[1]) capRefs.current[1]!.style.opacity = `${win(t, 0.22, 0.29) * (1 - win(t, 0.34, 0.42))}`;
      if (capRefs.current[2]) capRefs.current[2]!.style.opacity = `${win(t, 0.56, 0.66)}`;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div ref={rootRef} className={`kgp-root relative w-full ${variant === 'light' ? 'kgp-light' : ''}`} style={{ aspectRatio: `${W} / 280` }}>
      <style>{`
        .kgp-node{position:absolute;transform:translate(-50%,-50%);white-space:nowrap;border-radius:.55rem;padding:.26rem .5rem;font-size:.68rem;font-weight:700;display:flex;align-items:center;gap:.3rem;will-change:left,top}
        .kgp-signal{background:rgba(249,115,22,.14);border:1px solid rgba(249,115,22,.6);color:#FDBA74}
        .kgp-person{background:#0F766E;color:#fff;border-radius:9999px}
        .kgp-person.kgp-over{background:#F97316;box-shadow:0 0 16px 2px rgba(249,115,22,.6)}
        .kgp-ticket{background:#fff;border:1px solid #cbd5e1;color:#334155}
        .kgp-ticket.kgp-risk{border-color:#F97316}
        .kgp-sprint{background:#0B302D;color:#fff;border:2px solid #F97316;border-radius:.7rem;padding:.4rem .55rem;font-size:.72rem}
        .kgp-ava{width:1.05rem;height:1.05rem;border-radius:9999px;background:rgba(255,255,255,.25);color:#fff;font-size:.5rem;font-weight:800;display:flex;align-items:center;justify-content:center}
        .kgp-colhd{position:absolute;transform:translate(-50%,-50%);font-size:.72rem;font-weight:900;text-transform:uppercase;letter-spacing:.06em;color:#5EEAD4;white-space:nowrap;will-change:opacity}
        .kgp-decoy{position:absolute;transform:translate(-50%,-50%);width:6px;height:6px;border-radius:9999px;background:rgba(94,234,212,.5);will-change:opacity}
        .kgp-scan{position:absolute;top:0;height:100%;width:80px;pointer-events:none;background:linear-gradient(90deg,transparent,rgba(94,234,212,.35),transparent);will-change:left,opacity;opacity:0}
        .kgp-flow{stroke-dasharray:11 7;animation:kgpFlow 1.1s linear infinite}
        @keyframes kgpFlow{to{stroke-dashoffset:-18}}
        @media (prefers-reduced-motion:reduce){.kgp-flow{animation:none;stroke-dasharray:none}}
        /* ── light variant: the marketing register (white card) ── */
        .kgp-light .kgp-signal{background:#FFF4E8;border:1px solid rgba(217,119,6,.55);color:#B45309}
        .kgp-light .kgp-ticket{background:#fff;border:1px solid #CBD5E1;color:#334155;box-shadow:0 2px 8px rgba(11,59,56,.10)}
        .kgp-light .kgp-ticket.kgp-risk{border-color:#F97316}
        .kgp-light .kgp-sprint{background:#0B3B38;border:2px solid #F97316;color:#fff;box-shadow:0 6px 18px rgba(11,59,56,.25)}
        .kgp-light .kgp-colhd{color:#C2410C}
        .kgp-light .kgp-decoy{background:rgba(15,118,110,.30)}
        .kgp-light .kgp-scan{background:linear-gradient(90deg,transparent,rgba(15,118,110,.14),transparent)}
        .kgp-light .kgp-cap{color:#46706A}
        /* ── vertical lane mode (.kgp-vert is toggled by the frame loop when
              the rendered width drops below MOBILE_BREAK): taller canvas,
              modestly smaller node chrome, captions hidden (they collide with
              the stacked rows). The row labels become OPAQUE CHIPS — the lane
              lines pass BEHIND them (labeled-bus idiom), so the words are
              always readable by construction, never by coordinate luck. ── */
        .kgp-vert.kgp-root{aspect-ratio:560/460 !important}
        .kgp-vert .kgp-node{font-size:.6rem;padding:.2rem .4rem;gap:.22rem;border-radius:.45rem}
        .kgp-vert .kgp-sprint{font-size:.64rem;padding:.3rem .45rem;border-width:1.5px}
        .kgp-vert .kgp-ava{width:.9rem;height:.9rem;font-size:.45rem}
        .kgp-vert .kgp-colhd{font-size:.6rem;letter-spacing:.05em;padding:.2rem .65rem;
          border-radius:9999px;background:#06302B;box-shadow:0 0 0 1px rgba(94,234,212,.22)}
        .kgp-light.kgp-vert .kgp-colhd{background:#FFFFFF;box-shadow:0 0 0 1px rgba(194,65,12,.22)}
        .kgp-vert .kgp-cap{display:none}
      `}</style>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        {EXTRA.map((_, i) => <path key={`e${i}`} ref={el => { extraRefs.current[i] = el; }} fill="none" strokeLinecap="round" />)}
        {STORY.map((_, i) => <path key={`s${i}`} ref={el => { storyRefs.current[i] = el; }} fill="none" strokeLinecap="round" className="kgp-flow" />)}
      </svg>
      <div ref={scanRef} className="kgp-scan" />
      {DECOYS.map((d, i) => <div key={i} ref={el => { decoyRefs.current[i] = el; }} className="kgp-decoy" style={{ left: `${(d.x / W) * 100}%`, top: `${(d.y / H) * 100}%` }} />)}
      {HEADERS.map((h, i) => <div key={h.label} ref={el => { hdrRefs.current[i] = el; }} className="kgp-colhd" style={{ left: `${(h.x / W) * 100}%`, top: `${(40 / H) * 100}%`, opacity: 0 }}>{h.label}</div>)}
      {NODES.map(n => <div key={n.id} ref={el => { nodeRefs.current[n.id] = el; }} className={`kgp-node ${nodeClass(n.type)}`} dangerouslySetInnerHTML={{ __html: n.html }} />)}
      {['Your delivery data — everything connected.', 'Avium reads every signal…', '…the risks that need you today.'].map((c, i) => (
        <div key={i} ref={el => { capRefs.current[i] = el; }} className="kgp-cap absolute left-0 right-0 text-center text-[13px] text-teal-50/90" style={{ bottom: -6, opacity: 0 }}>{c}</div>
      ))}
    </div>
  );
}
